import { Box, Button, Checkbox, Grid, Typography } from "@mui/material";
import { COLORS } from "assets";
import { MODEL_LABELS, MODELS, modelSupportsAudio, normalizeActiveModelMedia } from "config/constants";
import { ActiveModel, ModelTypes } from "types";
import NoDetectionModels from "./NoDetectionModels";

export interface ActivationModelGridProps {
  enabledModels: ModelTypes[];
  activeModels: ActiveModel[];
  onChange: (model: ModelTypes, video: boolean, audio: boolean) => void;
  title: string;
  description: string;
  hint?: string;
  videoLabel: string;
  audioLabel: string;
  mediaProcessingLabel: string;
  t: Record<string, string>;
}

function MediaPill({
  label,
  selected,
  disabled,
  onClick
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        minWidth: 56,
        height: 28,
        px: 1.5,
        borderRadius: `${COLORS.radius.sm}px`,
        fontSize: 12,
        fontWeight: 500,
        textTransform: "none",
        border: `1px solid ${selected ? COLORS.primary : COLORS.surface.border}`,
        bgcolor: selected ? COLORS.primary : "transparent",
        color: selected ? COLORS.white : COLORS.surface.secondaryText,
        boxShadow: "none",
        "&:hover": {
          bgcolor: selected ? COLORS.primary : COLORS.surface.tableHeader,
          boxShadow: "none"
        },
        "&.Mui-disabled": {
          borderColor: COLORS.surface.border,
          color: COLORS.surface.mutedText
        }
      }}
    >
      {label}
    </Button>
  );
}

export default function ActivationModelGrid({
  enabledModels,
  activeModels,
  onChange,
  title,
  description,
  hint,
  videoLabel,
  audioLabel,
  mediaProcessingLabel,
  t
}: ActivationModelGridProps) {
  if (!enabledModels.length) {
    return (
      <Box>
        <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 14, mb: 1.5 }}>
          {title}
        </Typography>
        <NoDetectionModels />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 14, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 13, lineHeight: 1.5, mb: 1.5 }}>
        {description}
      </Typography>
      <Grid container spacing={1.5}>
        {enabledModels.map((elm) => {
          const i = MODELS.indexOf(elm);
          const model = activeModels.find((item) => item.model === elm);
          const isActive = model?.model === elm;
          const videoOn = Boolean(model?.video);
          const audioOn = Boolean(model?.audio);
          const supportsAudio = modelSupportsAudio(elm);

          return (
            <Grid item xs={12} sm={6} key={elm}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: `${COLORS.radius.md}px`,
                  border: `1px solid ${isActive ? COLORS.primary : COLORS.surface.border}`,
                  bgcolor: isActive ? "rgba(30, 152, 217, 0.04)" : COLORS.surface.card,
                  height: "100%"
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                  <Checkbox
                    size="small"
                    checked={isActive && (supportsAudio ? videoOn || audioOn : videoOn)}
                    indeterminate={supportsAudio && isActive && videoOn !== audioOn}
                    onChange={(_, checked) => {
                      if (supportsAudio) {
                        onChange(elm, checked, checked);
                      } else {
                        onChange(elm, checked, false);
                      }
                    }}
                    sx={{
                      p: 0.5,
                      color: COLORS.surface.secondaryText,
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: COLORS.primary }
                    }}
                  />
                  <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 500, fontSize: 13 }}>
                    {t[MODEL_LABELS[i]]}
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: COLORS.surface.secondaryText, fontSize: 11, fontWeight: 500, mb: 0.75, ml: 0.5 }}
                >
                  {mediaProcessingLabel}
                </Typography>
                <Box display="flex" gap={0.75} ml={0.5}>
                  <MediaPill
                    label={videoLabel}
                    selected={videoOn}
                    disabled={!isActive}
                    onClick={() => {
                      const next = normalizeActiveModelMedia(elm, !videoOn, audioOn);
                      onChange(elm, next.video, next.audio);
                    }}
                  />
                  {supportsAudio ? (
                    <MediaPill
                      label={audioLabel}
                      selected={audioOn}
                      disabled={!isActive}
                      onClick={() => {
                        const next = normalizeActiveModelMedia(elm, videoOn, !audioOn);
                        onChange(elm, next.video, next.audio);
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      {hint ? (
        <Typography sx={{ color: COLORS.surface.mutedText, fontSize: 12, mt: 1.25, lineHeight: 1.5 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}
